import { useState, useEffect, useRef, useMemo, ChangeEvent } from 'react'
import { v4 as uuidv4 } from 'uuid'
import update from 'immutability-helper'

export type ProductImageFormType = {
  isUseRealProductImage: boolean
  images: Array<{
    id: Nullable<number | string>
    key: string
    file: Nullable<File>
    previewUrl: string
    pathLarge: string
    pathMedium: string
    pathSmall: string
    pathReal: string
    urlLarge: string
    urlMedium: string
    urlSmall: string
    urlReal: string
    blurHash: string
  }>
}

export type ProductUploadImagePropsType = {
  errorMessage?: string | string[]
  value?: ProductImageFormType
  onChange?: (value: ProductImageFormType) => void
}

export const DEFAULT_PRODUCT_IMAGE_URL = {
  url_small: `${process.env.REACT_APP_PLATFORM_IMAGE_BASE_URL}/${process.env.REACT_APP_DEFAULT_PRODUCT_IMAGE_SMALL}`,
  url_medium: `${process.env.REACT_APP_PLATFORM_IMAGE_BASE_URL}/${process.env.REACT_APP_DEFAULT_PRODUCT_IMAGE_MEDIUM}`,
  url_large: `${process.env.REACT_APP_PLATFORM_IMAGE_BASE_URL}/${process.env.REACT_APP_DEFAULT_PRODUCT_IMAGE_LARGE}`,
}

export const MAX_IMAGE_UPLOAD = 5

export const getInitialImageState = (): ProductImageFormType['images'][number] => ({
  id: null,
  key: uuidv4(),
  file: null,
  previewUrl: '',
  pathLarge: '',
  pathMedium: '',
  pathSmall: '',
  pathReal: '',
  urlLarge: '',
  urlMedium: '',
  urlSmall: '',
  urlReal: '',
  blurHash: '',
})

export const getFilePath = (urlImage: string) => {
  const searchWords = `${process.env.REACT_APP_PLATFORM_IMAGE_BASE_URL}/`

  return urlImage.slice(searchWords.length)
}

export const useProductUploadImage = ({ value, onChange }: ProductUploadImagePropsType) => {
  const [productImageForm, setProductImageForm] = useState<ProductImageFormType>({
    isUseRealProductImage: false,
    images: [],
  })
  const [currentImageIndex, setCurrentImageIndex] = useState(-1)
  const [confirmationDeleteDialogOpen, setConfirmationDeleteDialogOpen] = useState(false)
  const fileRefInput = useRef<HTMLInputElement>(null)

  const totalExistedImage = useMemo(
    () => productImageForm.images.filter((image) => image.previewUrl).length,
    [productImageForm.images],
  )

  useEffect(() => {
    setProductImageForm(
      value || {
        isUseRealProductImage: false,
        images: [],
      },
    )
  }, [value])

  const handleDeleteImage = (index: number) => {
    setCurrentImageIndex(index)
    setConfirmationDeleteDialogOpen(true)
  }

  const handleChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    if (currentImageIndex === -1 || typeof onChange !== 'function') return

    if (event?.target.files) {
      const file = event.target.files[0]

      const reader = new FileReader()

      reader.onload = (e: ProgressEvent<FileReader>) => {
        onChange(
          update(productImageForm, {
            images: {
              [currentImageIndex]: {
                $merge: {
                  file,
                  previewUrl: e.target?.result as string,
                },
              },
            },
          }),
        )
        setCurrentImageIndex(-1)
        if (fileRefInput.current) {
          fileRefInput.current.value = ''
        }
      }

      reader.readAsDataURL(file)
    }
  }

  const handleClickBoxImage = (index: number) => {
    if (productImageForm.images[index]?.previewUrl || !fileRefInput.current) return
    setCurrentImageIndex(index)
    fileRefInput.current.click()
  }

  const handleMoveImage = (sourceIndex: number, destinationIndex: number) => {
    if (typeof onChange !== 'function') return

    onChange(
      update(productImageForm, {
        images: {
          $splice: [
            [sourceIndex, 1],
            [destinationIndex, 0, productImageForm.images[sourceIndex]],
          ],
        },
      }),
    )
  }

  const handleConfirmDeleteImage = () => {
    if (currentImageIndex === -1 || typeof onChange !== 'function') return

    onChange(
      update(productImageForm, {
        images: {
          $splice: [[currentImageIndex, 1]],
          $push: [getInitialImageState()],
        },
      }),
    )
    handleCloseConfirmationDeleteImage()
  }

  const handleCloseConfirmationDeleteImage = () => {
    setCurrentImageIndex(-1)
    setConfirmationDeleteDialogOpen(false)
  }

  const handleToggleUploadProductImage = () => {
    if (typeof onChange !== 'function') return

    onChange(
      update(productImageForm, {
        isUseRealProductImage: {
          $set: !productImageForm.isUseRealProductImage,
        },
      }),
    )
  }

  return {
    totalExistedImage,
    productImageForm,
    fileRefInput,
    confirmationDeleteDialogOpen,
    handleDeleteImage,
    handleChangeFile,
    handleClickBoxImage,
    handleMoveImage,
    handleConfirmDeleteImage,
    handleCloseConfirmationDeleteImage,
    handleToggleUploadProductImage,
  }
}
